var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { NetworkLocalOnly, LayoutCreationFinishedMessage, BusyIndicator, DeleteModelMessage, ModuleId, changeQueryParamter, CreateSubModelMessage, RemoveModelMessage, RemoveFacetFromModelMessage, UpdateFacetOnModelMessage, RoomChangedMessage, AvatarStyle, NotInSessionMessage } from "flavr-common";
import { StartSessionMessage, SessionConnectMessage, SessionDisconnectMessage, UserAuthenticatedMessage, ServerConnectionClosedMessage, PeerCurrentFineshedMessage } from "@messages/session/SessionMessages";
import { LocalUserInfoMessage } from "@messages/session/PeerMessages";
import { assertIsWeb } from "@messages/environment";
import { Module, isSessionMessage, isMessageFromModule } from "@modules/module";
import { modelIsSessionModel, modelPool, isModelPartOfScene, modelToAbsolutePath } from "@modules/model";
import { camera } from "@modules/rendering/camera";
import { preferencesService } from "@modules/preferences/PreferencesService";
import { sessionManagerService } from "./SessionManagerService";
import { serverConnection } from "../server/ServerConnectionService";
import { usersService } from "../UsersService";
import { peerService } from "../peer";
assertIsWeb("SessionModule");
/**
 * Owns the client-side session state and decides which room, model and peer messages need to be mirrored to or from the server.
 *
 * Messages:
 * - `RoomChangedMessage` - Updates the browser location's `room` query parameter to match the room the local user just entered (ignored for session-echoed room changes).
 * - `CreateSubModelMessage`, `RemoveModelMessage`, `DeleteModelMessage`, `UpdateFacetOnModelMessage` (unless immediately followed by a `BusyIndicator`), `RemoveFacetFromModelMessage`, `LayoutCreationFinishedMessage` and `NetworkLocalOnly` - Mirror model changes to the server when the model belongs to the active session, is part of the scene, and the change didn't already originate from the server, state manager or replay; `NetworkLocalOnly` additionally marks the model as local-only so later changes beneath it are skipped instead of being sent.
 * - `NotInSessionMessage` and `PeerCurrentFineshedMessage` - Handle session bootstrap: warn when no session exists, or mark the session connected, record whether it was reset, and send `StartSessionMessage` for the room once initial peer sync has finished.
 * - `ServerConnectedMessage`, `ServerSessionCurrentMessage` and `ServerSessionDisconnectMessage` - Received over the raw server connection rather than the bus; authenticate and publish the local user's identity/avatar, apply session-provided camera position/orientation and mic/camera/positional-audio preference defaults, and forward connect/disconnect notifications via `SessionConnectMessage`/`SessionDisconnectMessage`.
 */
export class SessionModule extends Module {
    /**
     * Wires up the server connection delegates and subscribes to the room, model and session bus messages that need
     * to be mirrored to or from the server, including a model-pool destroy hook that mirrors permanent deletions.
     */
    constructor() {
        super(ModuleId.SESSION);
        this.clientId = "";
        this.clientRoles = [];
        this.localOnlyModels = [];
        this.handleModelMessage = (msg, meta) => {
            if (!sessionManagerService.isConnectedToSession) {
                // ToDo: save to pending Messages
                return;
            }
            if (isSessionMessage(meta)) {
                return;
            }
            if (meta.transport === "local" || meta.transport === "internal") {
                return;
            }
            if (isMessageFromModule(meta, ModuleId.STATE_MANAGER) || isMessageFromModule(meta, ModuleId.REPLAY)) {
                return;
            }
            const model = modelPool.getModel(msg.uid);
            if (model === undefined)
                return;
            if (!modelIsSessionModel(model))
                return;
            if (this.ignoreModelMessage(model)) {
                return;
            }
            if (isModelPartOfScene(model)) {
                if (NetworkLocalOnly.is(msg)) {
                    if (!this.localOnlyModels.includes(model.getUID())) {
                        this.localOnlyModels.push(model.getUID());
                    }
                }
                this.sendBusMessageToServer(msg);
            }
        };
        this.handleRoomChangedMessage = (msg, meta) => {
            if (isSessionMessage(meta))
                return;
            const newRoom = modelPool.getModel(msg.uid);
            if (newRoom === undefined)
                return;
            history.replaceState(null, "", changeQueryParamter(location.search, "room", modelToAbsolutePath(newRoom)));
        };
        this.onServerOpen = () => {
            serverConnection.waitForServerInitData = true;
        };
        this.onConnectionToServerLost = (code) => {
            serverConnection.waitForServerInitData = false;
            if (this.connection.isReady()) {
                this.connection.send(new ServerConnectionClosedMessage(code));
            }
            else {
                this.hasServerConnectionLose = code;
            }
        };
        this.handleServerMessage = (msg) => {
            switch (msg.channel) {
                case "connected":
                    this.handleConnected(msg);
                    break;
                case "session:current":
                    this.handleCurrent(msg);
                    break;
                case "session:disconnect":
                    this.handleDisconnect(msg);
                    break;
            }
        };
        this.handlePeerCurrentFinishedMessage = (msg) => __awaiter(this, void 0, void 0, function* () {
            sessionManagerService.isConnectedToSession = true;
            sessionManagerService.wasSessionReset = (sessionManagerService.currentSessionName !== undefined && sessionManagerService.currentSessionName === sessionManagerService.pendingSessionName);
            sessionManagerService.currentSessionName = sessionManagerService.pendingSessionName;
            this.handleConnect();
            this.connection.send(new StartSessionMessage(msg.roomId));
            serverConnection.waitForServerInitData = false;
        });
        this.handleNotInSessionMessage = () => {
            console.warn("[multiplayer] You have no session!");
        };
        serverConnection.messageDelegate.add(this.handleServerMessage);
        serverConnection.openDelegate.add(this.onServerOpen);
        serverConnection.closeDelegate.add(this.onConnectionToServerLost);
        this.connection.subscribeByType(RoomChangedMessage, this.handleRoomChangedMessage);
        this.connection.subscribeByType(CreateSubModelMessage, this.handleModelMessage);
        this.connection.subscribeByType(RemoveModelMessage, this.handleModelMessage);
        this.connection.subscribeByType(DeleteModelMessage, this.handleModelMessage);
        // Match only if UpdateFacetOnModelMessage is not followed by BusyIndicator
        this.connection.subscribeByType(UpdateFacetOnModelMessage, this.handleModelMessage, `(?!${BusyIndicator.typestatic})`);
        this.connection.subscribeByType(RemoveFacetFromModelMessage, this.handleModelMessage);
        this.connection.subscribeByType(LayoutCreationFinishedMessage, this.handleModelMessage);
        this.connection.subscribeByType(NetworkLocalOnly, this.handleModelMessage);
        this.connection.subscribeByType(NotInSessionMessage, this.handleNotInSessionMessage);
        this.connection.subscribeByType(PeerCurrentFineshedMessage, this.handlePeerCurrentFinishedMessage);
        modelPool.setOnModelDestroyedHandler((model, destroyPermanently, meta) => {
            if (destroyPermanently)
                this.handleModelMessage(new DeleteModelMessage(model.getUID()), meta);
        });
    }
    /**
     * Sends the local user's authentication info to the server (if already known), initializes the session manager,
     * and re-sends a pending `ServerConnectionClosedMessage` if the connection dropped before the module was ready.
     */
    init() {
        const user = usersService.getLocalUser();
        if ((user === null || user === void 0 ? void 0 : user.identity) !== undefined) {
            this.connection.send(new UserAuthenticatedMessage(user.identity.firstname, user.identity.lastname, this.clientRoles, user.avatar.imageUrl, user.avatar.baseColor));
        }
        sessionManagerService.init();
        if (this.hasServerConnectionLose !== undefined) {
            const code = this.hasServerConnectionLose;
            delete this.hasServerConnectionLose;
            if (serverConnection.isServerOnline()) {
                this.connection.send(new ServerConnectionClosedMessage(code));
            }
        }
    }
    /**
     * Returns `true` if `model` is a descendant of (or equal to) a model previously marked local-only via
     * `NetworkLocalOnly`, meaning its changes must not be mirrored to the server.
     */
    ignoreModelMessage(model) {
        for (const parentModelUID of this.localOnlyModels) {
            const parentModel = modelPool.getModel(parentModelUID);
            if (parentModel === undefined)
                return true;
            if (parentModel.isAncestorOfOrEquals(model)) {
                return true;
            }
        }
        return false;
    }
    /**
     * Forwards a bus message to the server, optionally using the provided timestamp so the server-side ordering can match the local event time.
     */
    sendBusMessageToServer(msg, timestamp) {
        serverConnection.queueBusMessage(msg, timestamp);
    }
    handleConnected(data) {
        const userInfo = data.payload.info;
        this.clientId = data.payload.clientId;
        this.clientRoles = userInfo.roles;
        sessionManagerService.sessionIsResetting = false;
        sessionManagerService.sessionIsResetting = !!(sessionManagerService.currentSessionName && sessionManagerService.currentSessionName === sessionManagerService.pendingSessionName);
        peerService.setLocalClientId(this.clientId);
        const avatar = {
            imageUrl: userInfo.avatar.imageUrl,
            baseColor: userInfo.avatar.baseColor,
            style: AvatarStyle.Monitor
        };
        usersService.setLocalUser({
            clientId: this.clientId,
            clientIdx: data.payload.clientIdx,
            identity: userInfo.identity ? Object.assign({}, userInfo.identity) : undefined,
            avatar,
            guest: userInfo.guest
        });
        this.connection.send(new LocalUserInfoMessage(this.clientId, data.payload.clientIdx, avatar, userInfo.identity));
        if (this.connection.isReady() && userInfo.identity) {
            this.connection.send(new UserAuthenticatedMessage(userInfo.identity.firstname, userInfo.identity.lastname, userInfo.roles, userInfo.avatar.imageUrl, userInfo.avatar.baseColor));
        }
        if (sessionManagerService.needsSendSessionInfo()) {
            sessionManagerService.sendClientInfo(sessionManagerService.sessionName);
        }
    }
    handleCurrent(data) {
        if (!sessionManagerService.wasSessionReset) {
            if (data.payload.cameraPosition) {
                camera.perspectiveCam.position.fromArray(data.payload.cameraPosition);
            }
            if (data.payload.cameraOrientation) {
                camera.perspectiveCam.quaternion.fromArray(data.payload.cameraOrientation);
            }
        }
        if (data.payload.configs) {
            // Mic is key for speakers and microphone
            // => But why? Why combine two settings that are seperated everywhere else?
            const microphoneAndSpeakersDefaultEnabled = data.payload.configs.mic;
            const cameraDefaultEnabled = data.payload.configs.cam;
            const positionalAudioEnabled = data.payload.configs.positionalAudio;
            if (microphoneAndSpeakersDefaultEnabled !== undefined) {
                console.warn(`[multiplayer] SessionModule.handleCurrent(ServerSessionCurrentMessage):\n microphoneAndSpeakersDefaultEnabled = ${String(microphoneAndSpeakersDefaultEnabled)}`);
                preferencesService.setMemoryPreference("microphoneDefaultEnabled", microphoneAndSpeakersDefaultEnabled);
                preferencesService.setMemoryPreference("speakersDefaultEnabled", microphoneAndSpeakersDefaultEnabled);
            }
            if (cameraDefaultEnabled !== undefined) {
                preferencesService.setMemoryPreference("cameraDefaultEnabled", cameraDefaultEnabled);
            }
            if (positionalAudioEnabled !== undefined) {
                preferencesService.setMemoryPreference("disablePositionalAudio", !positionalAudioEnabled);
            }
        }
    }
    handleConnect() {
        this.connection.send(new SessionConnectMessage(this.clientId));
    }
    handleDisconnect(data) {
        const clientId = data.payload.clientId;
        const clientIdx = data.payload.clientIdx;
        this.connection.send(new SessionDisconnectMessage(clientId, clientIdx));
    }
}
